import type { SeiFrame } from './sei';

export const GEAR_LABELS = ['P', 'D', 'R', 'N'] as const;
export const AUTOPILOT_LABELS = ['Manual', 'Self-driving', 'Autopilot', 'TACC'] as const;

export const SIGNAL_BRAKE = 1;
export const SIGNAL_BLINKER_LEFT = 2;
export const SIGNAL_BLINKER_RIGHT = 4;

const SPEED_NOISE_MPS = 0.05;
const MAX_STEERING_STEP_DEG = 60;
const TRACE_STEP_S = 0.1;
const TIME_EPSILON_S = 1e-4;
const BIN_EPSILON = 1e-6;

export interface Telemetry {
  readonly time: Float64Array;
  readonly seq: Float64Array;
  readonly speed: Float32Array;
  readonly pedal: Float32Array;
  readonly steering: Float32Array;
  readonly heading: Float32Array;
  readonly accelX: Float32Array;
  readonly accelY: Float32Array;
  readonly accelZ: Float32Array;
  readonly latitude: Float64Array;
  readonly longitude: Float64Array;
  readonly gear: Uint8Array;
  readonly autopilot: Uint8Array;
  readonly signals: Uint8Array;
}

export interface Trace {
  readonly start: number;
  readonly step: number;
  readonly speedMin: Float32Array;
  readonly speedMax: Float32Array;
  readonly accelYMin: Float32Array;
  readonly accelYMax: Float32Array;
  readonly accelXMin: Float32Array;
  readonly accelXMax: Float32Array;
  readonly brake: Uint8Array;
  readonly heading: Float32Array;
  readonly latitude: Float64Array;
  readonly longitude: Float64Array;
}

export interface TimedFrame {
  readonly time: number;
  readonly frame: SeiFrame;
}

const signalsOf = (frame: SeiFrame): number =>
  (frame.brake ? SIGNAL_BRAKE : 0) | (frame.blinkerLeft ? SIGNAL_BLINKER_LEFT : 0) | (frame.blinkerRight ? SIGNAL_BLINKER_RIGHT : 0);

const allocate = (count: number): Telemetry => ({
  time: new Float64Array(count),
  seq: new Float64Array(count),
  speed: new Float32Array(count),
  pedal: new Float32Array(count),
  steering: new Float32Array(count),
  heading: new Float32Array(count),
  accelX: new Float32Array(count),
  accelY: new Float32Array(count),
  accelZ: new Float32Array(count),
  latitude: new Float64Array(count),
  longitude: new Float64Array(count),
  gear: new Uint8Array(count),
  autopilot: new Uint8Array(count),
  signals: new Uint8Array(count),
});

export function buildTelemetry(frames: readonly TimedFrame[]): Telemetry {
  const sorted = [...frames].sort((a, b) => a.time - b.time);
  const telemetry = allocate(sorted.length);
  sorted.forEach(({ time, frame }, i) => {
    const previousSteering = i > 0 ? telemetry.steering[i - 1]! : frame.steering;
    const located = frame.latitude !== 0 && frame.longitude !== 0;
    telemetry.time[i] = time;
    telemetry.seq[i] = frame.frameSeqNo;
    telemetry.speed[i] = frame.speed < SPEED_NOISE_MPS ? 0 : frame.speed;
    telemetry.pedal[i] = frame.pedal;
    telemetry.steering[i] = Math.abs(frame.steering - previousSteering) > MAX_STEERING_STEP_DEG ? previousSteering : frame.steering;
    telemetry.heading[i] = frame.heading;
    telemetry.accelX[i] = frame.accelX;
    telemetry.accelY[i] = frame.accelY;
    telemetry.accelZ[i] = frame.accelZ;
    telemetry.latitude[i] = located ? frame.latitude : Number.NaN;
    telemetry.longitude[i] = located ? frame.longitude : Number.NaN;
    telemetry.gear[i] = frame.gear;
    telemetry.autopilot[i] = frame.autopilot;
    telemetry.signals[i] = signalsOf(frame);
  });
  return telemetry;
}

export function frameIndexAt(times: Float64Array, time: number): number {
  let low = 0;
  let high = times.length - 1;
  if (high < 0 || time < times[0]! - TIME_EPSILON_S) return -1;
  while (low < high) {
    const mid = (low + high + 1) >> 1;
    if (times[mid]! <= time + TIME_EPSILON_S) low = mid;
    else high = mid - 1;
  }
  return low;
}

export function timeOfSeq(telemetry: Telemetry, seq: number): number | undefined {
  const { seq: seqs, time } = telemetry;
  let low = 0;
  let high = seqs.length - 1;
  while (low <= high) {
    const mid = (low + high) >> 1;
    if (seqs[mid] === seq) return time[mid];
    if (seqs[mid]! < seq) low = mid + 1;
    else high = mid - 1;
  }
  return undefined;
}

const circularMean = (sin: number, cos: number): number => ((Math.atan2(sin, cos) * 180) / Math.PI + 360) % 360;

const allocateTrace = (start: number, bins: number): Trace => ({
  start,
  step: TRACE_STEP_S,
  speedMin: new Float32Array(bins).fill(Number.NaN),
  speedMax: new Float32Array(bins).fill(Number.NaN),
  accelYMin: new Float32Array(bins).fill(Number.NaN),
  accelYMax: new Float32Array(bins).fill(Number.NaN),
  accelXMin: new Float32Array(bins).fill(Number.NaN),
  accelXMax: new Float32Array(bins).fill(Number.NaN),
  brake: new Uint8Array(bins),
  heading: new Float32Array(bins).fill(Number.NaN),
  latitude: new Float64Array(bins).fill(Number.NaN),
  longitude: new Float64Array(bins).fill(Number.NaN),
});

const widen = (min: Float32Array, max: Float32Array, bin: number, value: number): void => {
  min[bin] = Number.isNaN(min[bin]!) ? value : Math.min(min[bin]!, value);
  max[bin] = Number.isNaN(max[bin]!) ? value : Math.max(max[bin]!, value);
};

interface TraceSums {
  readonly sin: Float64Array;
  readonly cos: Float64Array;
  readonly latitude: Float64Array;
  readonly longitude: Float64Array;
  readonly located: Uint32Array;
}

const allocateSums = (bins: number): TraceSums => ({
  sin: new Float64Array(bins),
  cos: new Float64Array(bins),
  latitude: new Float64Array(bins),
  longitude: new Float64Array(bins),
  located: new Uint32Array(bins),
});

function accumulate(trace: Trace, sums: TraceSums, telemetry: Telemetry, i: number, bin: number): void {
  widen(trace.speedMin, trace.speedMax, bin, telemetry.speed[i]!);
  widen(trace.accelYMin, trace.accelYMax, bin, telemetry.accelY[i]!);
  widen(trace.accelXMin, trace.accelXMax, bin, telemetry.accelX[i]!);
  trace.brake[bin]! |= telemetry.signals[i]! & SIGNAL_BRAKE;
  const radians = (telemetry.heading[i]! * Math.PI) / 180;
  sums.sin[bin]! += Math.sin(radians);
  sums.cos[bin]! += Math.cos(radians);
  if (Number.isNaN(telemetry.latitude[i]!)) return;
  sums.latitude[bin]! += telemetry.latitude[i]!;
  sums.longitude[bin]! += telemetry.longitude[i]!;
  sums.located[bin]!++;
}

function finalize(trace: Trace, sums: TraceSums): void {
  for (let bin = 0; bin < trace.brake.length; bin++) {
    if (Number.isNaN(trace.speedMin[bin]!)) continue;
    trace.heading[bin] = circularMean(sums.sin[bin]!, sums.cos[bin]!);
    if (sums.located[bin]! === 0) continue;
    trace.latitude[bin] = sums.latitude[bin]! / sums.located[bin]!;
    trace.longitude[bin] = sums.longitude[bin]! / sums.located[bin]!;
  }
}

export function downsample(telemetry: Telemetry): Trace | undefined {
  const { time } = telemetry;
  if (time.length === 0) return undefined;
  const start = time[0]!;
  const binOf = (at: number): number => Math.floor((at - start) / TRACE_STEP_S + BIN_EPSILON);
  const bins = binOf(time.at(-1)!) + 1;
  const trace = allocateTrace(start, bins);
  const sums = allocateSums(bins);
  for (let i = 0; i < time.length; i++) accumulate(trace, sums, telemetry, i, binOf(time[i]!));
  finalize(trace, sums);
  return trace;
}
